import { InvitationStatus } from '@generated/graphql';
import type { Affiliation } from '@interfaces/affiliation';
import type { SxProps } from '@mui/material';
import { Box } from '@ovComponents/1-primative/box/box';
import { Link } from '@ovComponents/1-primative/link/link';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { CompletionBox } from '@ovComponents/3-pattern/completionBox/completionBox';
import { NumberField } from '@ovComponents/3-pattern/numberField/numberField';
import { useThemeTokens } from '@providers/themeTokenProvider';
import { formatMoneyValue } from '@utils/moneyFormatting';
import { useTranslation } from 'react-i18next';

const affiliateName = (affiliate: Affiliation) =>
  `${affiliate.user.firstName} ${affiliate.user.lastName}`;

export const AffiliateCompletionBox = ({
  testId = 'affiliate-completion-box',
  updateMode,
  affiliate,
  title,
  edittable,
  error,
  errorText,
  showRelationship,
  showAllocation,
  showGuardian = false,
  showPrimaryCaregiver = false,
  guardian = null,
  primaryCaregiver = null,
  supportText,
  supportUrl,
  createOrEditAffiliate,
  removeAffiliate,
  onChange,
  showContributionAmount = false,
  showGrantAmount = false,
  options,
  accountData,
  refetch,
}: {
  testId?: string;
  updateMode?: boolean;
  affiliate: Affiliation;
  title: string;
  edittable?: boolean;
  error?: boolean;
  errorText?: string;
  showRelationship?: boolean;
  showAllocation?: boolean;
  showGuardian?: boolean;
  showPrimaryCaregiver?: boolean;
  guardian?: Affiliation | null;
  primaryCaregiver?: Affiliation | null;
  supportText?: string;
  supportUrl?: string;
  createOrEditAffiliate: (
    action: 'create' | 'edit',
    affiliate?: Affiliation,
  ) => void;
  removeAffiliate: (affiliateId: string) => void;
  onChange?: (event: any, affiliateId: string) => void;
  showContributionAmount?: boolean;
  showGrantAmount?: boolean;
  options?: any;
  accountData?: any;
  refetch?: () => void;
}) => {
  const { sys } = useThemeTokens();
  const { t } = useTranslation(['affiliationTypes', 'client']);

  return (
    <CompletionBox
      testId={testId}
      variant={updateMode ? 'view' : 'edit'}
      state='complete'
      edittable={edittable}
      title={title}
      onEdit={() => createOrEditAffiliate('edit', affiliate)}
      onRemove={() => removeAffiliate(affiliate.id ?? '')}
      onAdd={() => createOrEditAffiliate('create', affiliate)}
      error={error}
      errorText={errorText}
      affiliate={affiliate}
      options={options}
      accountData={accountData}
      refetch={refetch}
    >
      <Typography mb={1} variant='bodyMedium' data-testid={`${testId}-name`}>
        <span style={{ color: sys.color.onSurfaceVariant }}>{t('name')}:</span>{' '}
        <b>{affiliateName(affiliate)}</b>
      </Typography>
      {showRelationship && (
        <Typography
          mb={1}
          variant='bodyMedium'
          data-testid={`${testId}-relationship`}
        >
          <span style={{ color: sys.color.onSurfaceVariant }}>
            {t('relationship')}:
          </span>{' '}
          <b>{t(`affiliationRelationship.${affiliate.relation}`)}</b>
        </Typography>
      )}
      {showGuardian && (
        <Typography
          mb={1}
          variant='bodyMedium'
          data-testid={`${testId}-guardian`}
        >
          <span style={{ color: sys.color.onSurfaceVariant }}>
            {t('affiliationTypes:GUARDIAN')}:
          </span>{' '}
          <b>{guardian ? affiliateName(guardian) : ''}</b>
        </Typography>
      )}
      {showPrimaryCaregiver && (
        <Typography
          mb={1}
          variant='bodyMedium'
          data-testid={`${testId}-primary-caregiver`}
        >
          <span style={{ color: sys.color.onSurfaceVariant }}>
            {t('affiliationTypes:PRIMARY_CAREGIVER')}:
          </span>{' '}
          <b>{primaryCaregiver ? affiliateName(primaryCaregiver) : ''}</b>
        </Typography>
      )}
      {showAllocation && edittable && (
        <>
          <Box
            bgcolor={sys.color.surfaceContainerVariant}
            borderRadius={sys.borderRadius.md}
            sx={allocationContainerSx}
            data-testid={`${testId}-allocation-box`}
          >
            <Typography
              variant='bodyMedium'
              data-testid={`${testId}-allocation-text`}
            >
              {t('allocation')}
            </Typography>
            <NumberField
              testId='affiliate-allocation'
              label={''}
              decimalPlaces={2}
              min={0}
              number={(affiliate.allocation || 0).toString()}
              size='small'
              trailingIcon='percent'
              sx={{ width: '100px' }}
              showArrows={false}
              setNumber={(value: string) =>
                onChange?.({ target: { value } }, affiliate.id ?? '')
              }
            />
          </Box>
          {affiliate.allocation === 0 && (
            <Typography
              mt={1}
              variant='bodySmall'
              color={sys.color.warning}
              data-testid={`${testId}-allocation-warning`}
            >
              {t('affiliationTypes:zeroAllocationWarning')}
            </Typography>
          )}
        </>
      )}
      {showAllocation && !edittable && (
        <Typography
          mb={1}
          variant='bodyMedium'
          data-testid={`${testId}-allocation`}
        >
          <span style={{ color: sys.color.onSurfaceVariant }}>
            {t('allocation')}:
          </span>{' '}
          <b>{affiliate.allocation}%</b>
        </Typography>
      )}
      {!edittable && supportText !== '' && (
        <Typography
          mt={1}
          variant='bodyMedium'
          data-testid={`${testId}-support`}
        >
          <Link
            target='_blank'
            href={
              supportUrl || 'https://onevest.zendesk.com/hc/en-us/requests/new'
            }
          >
            {supportText ?? t('contactSupport')}
          </Link>
        </Typography>
      )}

      {showContributionAmount && !edittable && (
        <Typography
          variant='bodyMedium'
          data-testid={`${testId}-contribution-amount`}
        >
          <span style={{ color: sys.color.onSurfaceVariant }}>
            {t('contributionAmount')}:
          </span>
          <b>
            {formatMoneyValue(
              affiliate?.contributionAmount ? affiliate.contributionAmount : 0,
              '$',
              2,
              false,
            )}
          </b>
        </Typography>
      )}

      {showGrantAmount && !edittable && (
        <Typography variant='bodyMedium' data-testid={`${testId}-grant-amount`}>
          <span style={{ color: sys.color.onSurfaceVariant }}>
            {t('grantAmount')}:
          </span>{' '}
          <b>
            {formatMoneyValue(
              affiliate.grantAmount ? affiliate.grantAmount : 0,
              '$',
              2,
              false,
            )}
          </b>
        </Typography>
      )}
      {affiliate?.invitationStatus === InvitationStatus.Failed && (
        <Typography
          mt={1}
          variant='bodySmall'
          color={sys.color.warning}
          data-testid={`${testId}-invitation-warning`}
        >
          {t('affiliationTypes:affiliateInvitationFailed')}
        </Typography>
      )}
      {affiliate?.invitationStatus === InvitationStatus.Sent && (
        <Typography
          mt={1}
          variant='bodySmall'
          color={sys.color.positive}
          data-testid={`${testId}-invitation-sent`}
        >
          {t('affiliationTypes:affiliateInvitationSent')}
        </Typography>
      )}
      {affiliate?.invitationStatus === InvitationStatus.Pending && (
        <Typography
          mt={1}
          variant='bodySmall'
          color={sys.color.positive}
          data-testid={`${testId}-invitation-pending`}
        >
          {t('affiliationTypes:affiliateInvitationPending')}
        </Typography>
      )}
    </CompletionBox>
  );
};

const allocationContainerSx: SxProps = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '4px 8px 8px 12px',
  ml: -1.5,
  mt: 0.5,
};
